<template>
  <v-dialog v-model="dialog" persistent max-width="1000px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="isEditButton" icon v-on="on">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark :disabled="!hasProcessingDays" v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>
          Add New
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h5">Cycle Set</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row >

            <v-col cols="12">
              <v-text-field
                label="Cycle Set Name"
                ref="cycleSetName"
                v-model="workingCycleSet.name"
                v-on:input="onModification()"
                :rules="[value => !!value || 'Required.']">
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-combobox label = "Statement Cycle Codes"
                ref="cycleCodes"
                multiple
                return-object
                v-model="workingCycleSet.statementCycleCodes"
                append-icon
                chips
                deletable-chips
                :rules="requiredRule"
                :disabled="workingCycleSet.processAllStatementCycleCodes"
                :search-input.sync="enteredCycleCodes"
                @update:search-input="splitIntoCycleCodes"
                @change="onModification">
              </v-combobox>
            </v-col>

            <v-col cols="12">
              <v-checkbox
                class="pa-0 ma-0"
                v-model="workingCycleSet.processAllStatementCycleCodes"
                ref="processAllStatementCycleCodes"
                label="Process for all Statement Cycle Codes"
                @change="onCheckboxModification()"
                >
              </v-checkbox>
            </v-col>

          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn ref="cancelButton" large @click="cancel" :disabled="saving">Cancel</v-btn>
          <v-btn ref="saveButton" large class="blue white--text" @click="save"
            :loading="saving" :disabled="!isModificationSavable">
              Save
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import {
  CycleSet,
  CycleResource,
} from '@/api/cycles';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';

@Component({
  computed: mapState(['fiId']),
})
export default class CycleSetDialog extends Vue {
  @Inject('CycleResource')
  cycleResource!:CycleResource;

  @Prop()
  hasProcessingDays!: boolean;

  @Prop()
  cycleSet!: CycleSet;

  @Prop({ default() { return []; } })
  existingCycleSets!:CycleSet[];

  @Prop()
  isEditButton!:boolean;

  workingCycleSet:CycleSet =
    CycleSetDialog.createEmptyCycleSet();

  fiId!:string;

  dialog:boolean = false;

  saving:boolean = false;

  isModificationSavable:boolean = false;

  loading = false;

  delimiter: string = ',';

  enteredCycleCodes:string = '';

  static ALL_STATEMENT_CODE = 'All Statement Cycle Codes'

  static createEmptyCycleSet() : CycleSet {
    return {
      id: undefined!,
      name: '',
      statementCycleCodes: [],
      processAllStatementCycleCodes: false,
      cyclesDesc: [],
    };
  }

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;
      this.loading = true;
      this.initializeWorkingCycleSet();
      this.loading = false;
    }
  }

  initializeWorkingCycleSet() {
    this.isModificationSavable = false;
    if (this.cycleSet) {
      Object.assign(this.workingCycleSet, this.cycleSet);
      this.workingCycleSet.statementCycleCodes = this.workingCycleSet.statementCycleCodes.sort();
    } else {
      this.workingCycleSet = CycleSetDialog.createEmptyCycleSet();
    }
  }

  cancel() {
    this.initializeWorkingCycleSet();
    this.dialog = false;
  }

  save() {
    // Force combos to update because they might still have focus when user clicks save.
    if (this.enteredCycleCodes && this.enteredCycleCodes.trim()
      && !this.workingCycleSet.statementCycleCodes.includes(this.enteredCycleCodes.trim())) {
      this.workingCycleSet.statementCycleCodes.push(this.enteredCycleCodes.trim());
    }

    this.saving = true;
    if (!this.isWorkingCycleNameUnique()) {
      this.saving = false;
      this.setError('Duplicate Cycle Set Name!');
      return;
    }
    if (this.hasCycleCodeConflict()) {
      this.saving = false;
      this.setError('One of the cycle sets covers All Statement Cycle Codes!');
      return;
    }
    if (!this.areWorkingCycleCodesUnique()) {
      this.saving = false;
      this.setError('Duplicate Statement Cycle Code!');
      return;
    }
    this.cycleResource.saveCycleSet(this.fiId, this.workingCycleSet)
      .then((savedCycleSet:CycleSet) => {
        this.saving = false;
        if (this.cycleSet) {
          Object.assign(this.cycleSet, savedCycleSet);
        }
        this.$emit('success', savedCycleSet);
        this.dialog = false;
        this.initializeWorkingCycleSet();
      })
      .catch((error:any) => {
        this.saving = false;
        if (error && error.response && error.response.status === 409) {
          this.setError('The cycle set you are trying to add conflicts with existing cycle set(s)');
        } else {
          this.setError("'Unexpected error in saving CycleSet");
        }
      });
  }

  hasCycleCodeConflict() {
    const allCycleCodes = new Set<string>();
    this.existingCycleSets.forEach((cycleSet) => {
      if (cycleSet.id !== this.workingCycleSet.id) {
        if (cycleSet.processAllStatementCycleCodes) {
          allCycleCodes.add(CycleSetDialog.ALL_STATEMENT_CODE);
        }
      }
    });
    if (allCycleCodes.has(CycleSetDialog.ALL_STATEMENT_CODE)
      || (this.existingCycleSets.length > 1
          && this.workingCycleSet.processAllStatementCycleCodes)) {
      return true;
    }
    return false;
  }

  isWorkingCycleNameUnique(): boolean {
    const allNames = new Set<string>();
    this.existingCycleSets.forEach((cycleSet) => {
      if (cycleSet.id !== this.workingCycleSet.id) {
        allNames.add(cycleSet.name);
      }
    });
    if (allNames.has(this.workingCycleSet.name)) {
      return false;
    }
    return true;
  }

  areWorkingCycleCodesUnique(): boolean {
    const allCycleCodes = new Set<string>();
    this.existingCycleSets.forEach((cycleSet) => {
      if (cycleSet.id !== this.workingCycleSet.id) {
        cycleSet.statementCycleCodes.forEach((cycleCode) => {
          allCycleCodes.add(cycleCode);
        });
      }
    });
    let isUnique = true;
    this.workingCycleSet.statementCycleCodes.forEach((cycleCode) => {
      if (allCycleCodes.has(cycleCode)) {
        isUnique = false;
      }
    });
    return isUnique;
  }

  onCheckboxModification() {
    if (this.workingCycleSet.processAllStatementCycleCodes) {
      this.workingCycleSet.statementCycleCodes = [];
    }
    this.onModification();
  }

  onModification() {
    const oldCodes = new Set<string>();
    if (this.cycleSet) {
      this.cycleSet.statementCycleCodes.forEach((cycleCode) => {
        oldCodes.add(cycleCode);
      });
    }
    const newCodes = new Set<string>();
    if (this.workingCycleSet && this.workingCycleSet.statementCycleCodes) {
      this.workingCycleSet.statementCycleCodes.forEach((cycleCode) => {
        newCodes.add(cycleCode);
      });
    }
    const deletedCodes = new Set([...oldCodes].filter(x => !newCodes.has(x)));
    const addedCodes = new Set([...newCodes].filter(x => !oldCodes.has(x)));

    this.isModificationSavable = (this.workingCycleSet.name !== '' // cycle set name is provided
        && this.cycleSet && this.cycleSet.name !== this.workingCycleSet.name) // cycle set name  modified
        || this.isCycleCodeModified(deletedCodes, addedCodes);
  }

  isCycleCodeModified(deletedCodes: Set<string>, addedCodes: Set<string>): boolean {
    return (this.cycleSet
              && !this.cycleSet.processAllStatementCycleCodes
              && ((deletedCodes.size > 0 // cycle code removed
                    && this.workingCycleSet.statementCycleCodes.length > 0) // non empty cycle code)
                  || addedCodes.size > 0 // cycle code added
                  || this.workingCycleSet.processAllStatementCycleCodes))

          || (this.cycleSet
              && this.cycleSet.processAllStatementCycleCodes
              && !this.workingCycleSet.processAllStatementCycleCodes
              && this.workingCycleSet.statementCycleCodes.length > 0) // non empty cycle code

          || (!this.cycleSet
              && (this.workingCycleSet.processAllStatementCycleCodes
                  || this.workingCycleSet.statementCycleCodes.length > 0));
  }

  splitIntoCycleCodes() {
    if (this.enteredCycleCodes && this.enteredCycleCodes.split(this.delimiter).length > 1) {
      this.workingCycleSet.statementCycleCodes.push(...this.enteredCycleCodes
        .split(this.delimiter).filter(term => !this.workingCycleSet.statementCycleCodes.includes(term) && term.trim() !== ''));
      this.enteredCycleCodes = '';
      this.onModification();
    }
  }

  requiredRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0
          && !this.workingCycleSet.processAllStatementCycleCodes)) {
      return 'Required';
    }
    return true;
  }];

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
